import type {
  EventOptions,
  ValidPropertyType,
} from "@amplitude/analytics-types"
import type { EventName, EventProperties } from "../types"

export async function initAmplitude({ appVersion }: { appVersion: string }) {
  if (
    typeof window === "undefined" ||
    !process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY
  ) {
    return
  }
  const { init } = await import("@amplitude/analytics-browser")
  await init(process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY, {
    // is true by default, but warns if not set explicitly
    defaultTracking: true,
    appVersion,
    serverUrl:
      // We use a proxy to avoid ad blockers and so we're able to put waf on top of it and
      // block any restricted countries we otherwise block on opensea.io.
      //
      // To use the default server (so Amplitude extension will work), add this to .env.local:
      // NEXT_PUBLIC_AMPLITUDE_SERVER_URL=https://api2.amplitude.com/2/httpapi
      process.env.NEXT_PUBLIC_AMPLITUDE_SERVER_URL ||
      "https://atd2.openseaprorelayproxy.com",
    flushMaxRetries: 10,
    trackingOptions: {
      ipAddress: false,
    },
  }).promise
}

export const identify = async (
  userId: string | undefined,
  properties?: EventProperties,
) => {
  const {
    setUserId,
    Identify,
    identify: _identify,
  } = await import("@amplitude/analytics-browser")
  setUserId(userId)
  const id = new Identify()
  Object.entries(properties || {}).forEach(([key, value]) => {
    id.set(key, value as ValidPropertyType)
  })
  await _identify(id).promise
}

/**
 * Track an event with optional properties and options.
 * @param eventName - The name of the event to track, e.g. `"Link Click"`.
 * @param properties - Optional properties to include with the event.
 * @param options - Optional options to include with the event.
 */
export const track = async (
  eventName: EventName,
  properties?: EventProperties,
  options?: EventOptions,
) => {
  if (process.env.NODE_ENV === "development") {
    console.warn("[ANALYTICS]", eventName, properties)
  }
  const { track: _track } = await import("@amplitude/analytics-browser")
  await _track(eventName, properties, options).promise
}
